import { UTCTimestamp } from 'lightweight-charts';

type NumericRecord = { time: UTCTimestamp; value: number };

const normalizeSeriesValue = (value: unknown): number => {
  const numeric = Number(value ?? 0);
  return Number.isFinite(numeric) ? numeric : 0;
};

const smoothNumericSeries = <T extends NumericRecord>(records: T[]): T[] => {
  if (!records || records.length === 0) return records;

  const forwardFilled = records.map(record => ({
    ...record,
    value: normalizeSeriesValue(record.value),
  }));

  const DROP_RATIO_THRESHOLD = 0.1; // treat drops >90% as anomalies
  const SURGE_RATIO_THRESHOLD = 10; // treat spikes >10x as anomalies

  let lastNonZero: number | null = null;
  for (let i = 0; i < forwardFilled.length; i++) {
    const numeric = forwardFilled[i].value;
    if (numeric !== 0) {
      if (lastNonZero !== null && lastNonZero !== 0) {
        const ratio = numeric / lastNonZero;
        if (ratio < DROP_RATIO_THRESHOLD || ratio > SURGE_RATIO_THRESHOLD) {
          forwardFilled[i] = { ...forwardFilled[i], value: lastNonZero };
          continue;
        }
      }
      lastNonZero = forwardFilled[i].value;
      continue;
    }

    if (lastNonZero !== null) {
      forwardFilled[i] = { ...forwardFilled[i], value: lastNonZero };
    }
  }

  let nextNonZero: number | null = null;
  for (let i = forwardFilled.length - 1; i >= 0; i--) {
    const numeric = forwardFilled[i].value;
    if (numeric !== 0) {
      if (nextNonZero !== null && nextNonZero !== 0) {
        const ratio = numeric / nextNonZero;
        if (ratio < DROP_RATIO_THRESHOLD || ratio > SURGE_RATIO_THRESHOLD) {
          forwardFilled[i] = { ...forwardFilled[i], value: nextNonZero };
          continue;
        }
      }
      nextNonZero = forwardFilled[i].value;
      continue;
    }

    if (nextNonZero !== null) {
      forwardFilled[i] = { ...forwardFilled[i], value: nextNonZero };
    }
  }

  return forwardFilled;
};

export const smoothBalanceSeries = (
  series: { label: string; records: NumericRecord[] }[],
): { label: string; records: NumericRecord[] }[] => {
  if (!series || series.length === 0) return series;

  return series.map(iterator => ({
    ...iterator,
    records: smoothNumericSeries(iterator.records),
  }));
};

export const smoothTotalBalanceSeries = (records: NumericRecord[]): NumericRecord[] => {
  if (!records || records.length === 0) return records;
  return smoothNumericSeries(records);
};

export const normalizeBalanceValue = normalizeSeriesValue;
